/** @typedef {import('./BalancedNode').BalancedNode} BalancedNode */
/** @typedef {import('./DeviceGroupDevice').DeviceGroupDevice} DeviceGroupDevice */

import { buildSuccessorsMap, getPositionRange } from './balancedConstraints'
import { isExternalJobNode } from './balancedColumns'

/**
 * The line position a placed job currently occupies, stable across TYPE/LINE:
 * the assigned device's position in LINE, else the TYPE positionInLine.
 *
 * @param {BalancedNode} node
 * @returns {number|null}
 */
const currentLinePosition = (node) => {
  const ref = node.jobReference ?? {}
  return ref.preferenceDevice?.positionInLine ?? ref.positionInLine ?? null
}

/**
 * Whether a device supports a node's job technology.
 *
 * @param {DeviceGroupDevice} device
 * @param {BalancedNode} node
 * @returns {boolean}
 */
const deviceSupports = (device, node) => {
  const techId = node.jobReference?.technology?.id
  return (device?.technologies ?? []).some(t => t.id === techId)
}

/**
 * Remaps the current balancing assignment onto a new context (mode/line/positions)
 * instead of clearing it (HU-12). Each currently-placed JOB keeps its line position
 * when it still fits; otherwise it is unassigned. Never invents assignments —
 * unplaced jobs stay unplaced (R4).
 *
 * Rules:
 * - TYPE: keep at `linePosition` if `linePosition <= positionsCount`, else drop (S38/S39/S41).
 * - LINE: keep at the device whose `positionInLine === linePosition` iff it supports
 *   the job's technology, else drop (S40).
 * - Line advancement: after placement, drop any job outside its allowed range (S42).
 * - Story 146 / S66 (RN30.1): an EXTERNAL job is never a candidate. Not even a stale
 *   `jobOrder`/`positionInLine` left in the database before the node was marked puts it back in a
 *   column — and because it was never a candidate, it is not reported as "unassigned by the change"
 *   either. It does stay in `nodes`, so it keeps feeding the line-advancement graph.
 *
 * @param {BalancedNode[]} nodes - Current working nodes (raw, with real groups)
 * @param {{ balancedType: 'TYPE'|'LINE', positionsCount?: number, devices?: DeviceGroupDevice[] }} context
 * @returns {{ columnNodeIds: Object<string, string[]>, unassigned: string[] }}
 *   `columnNodeIds` maps columnKey → nodeId[] (jobOrder order); `unassigned` = names
 *   of jobs that were placed before but no longer fit.
 */
export const remapColumns = (nodes, context) => {
  const { balancedType, positionsCount, devices = [] } = context ?? {}
  const placed = (nodes ?? []).filter(n => n.nodeType === 'JOB' && !isExternalJobNode(n) && n.jobReference?.jobOrder != null)

  const kept = [] // { node, linePosition, columnKey }
  const unassigned = []

  for (const node of placed) {
    const linePosition = currentLinePosition(node)
    if (linePosition == null) { unassigned.push(node.name); continue }

    if (balancedType === 'TYPE') {
      const count = parseInt(positionsCount, 10)
      if (!isNaN(count) && linePosition <= count && linePosition >= 1) {
        kept.push({ node, linePosition, columnKey: String(linePosition) })
      } else {
        unassigned.push(node.name)
      }
    } else if (balancedType === 'LINE') {
      const device = devices.find(d => d.positionInLine === linePosition)
      if (device && deviceSupports(device, node)) {
        kept.push({ node, linePosition, columnKey: String(device.id) })
      } else {
        unassigned.push(node.name)
      }
    } else {
      unassigned.push(node.name)
    }
  }

  // Line-advancement pass (§6b): drop any kept job that now sits outside its range.
  const nodeById = Object.fromEntries((nodes ?? []).filter(n => n.nodeType === 'JOB').map(n => [n.id, n]))
  const successorsById = buildSuccessorsMap(nodes ?? [])
  const positionByNodeId = Object.fromEntries(kept.map(k => [k.node.id, k.linePosition]))
  const ctx = { nodeById, positionByNodeId, successorsById }
  const survivors = []
  for (const k of kept) {
    const { min, max } = getPositionRange(k.node, ctx)
    if (k.linePosition < min || k.linePosition > max) unassigned.push(k.node.name)
    else survivors.push(k)
  }

  const columnNodeIds = {}
  for (const k of survivors) {
    if (!columnNodeIds[k.columnKey]) columnNodeIds[k.columnKey] = []
    columnNodeIds[k.columnKey].push(k.node)
  }
  for (const key of Object.keys(columnNodeIds)) {
    columnNodeIds[key] = columnNodeIds[key]
      .sort((a, b) => (a.jobReference.jobOrder ?? 0) - (b.jobReference.jobOrder ?? 0))
      .map(n => n.id)
  }

  return { columnNodeIds, unassigned }
}
